import {
  Box,
  Card,
  Flex,
  IconButton,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Text,
  useSteps,
} from '@chakra-ui/react';
import { AdaptiveStack } from '@partiu/ui/components/atoms/AdaptiveStack.tsx';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ChooseDateStep } from './Steps/ChooseDateStep';
import { ChooseMethodStep } from './Steps/ChooseMethodStep';
import { ChooseReceiverStep } from './Steps/ChooseReceiverStep';
import { ChooseTemplateStep } from './Steps/ChooseTemplateStep';
import { ConfirmEmailStep } from './Steps/ConfirmEmailStep';
import { ConfirmPushStep } from './Steps/ConfirmPushStep';
import { EditMessageStep } from './Steps/EditMessageStep';
import { EditPushStep } from './Steps/EditPushStep';

import { useTheme } from '@partiu/ui/contexts/ThemeProvider';
import { DashboardTemplate } from '../../components/templates/DashboardTemplate';
import { NotificationCentralProvider, useNotificationCentralContext } from './notification-central.context';

const steps = [
  { title: 'Tipo de comunicação', description: 'Escolha o canal de comunicação' },
  { title: 'Destinatários', description: 'Quem vai receber a mensagem?' },
  { title: 'Template', description: 'Escolha um template' },
  { title: 'Mensagem', description: 'Edite a mensagem ou siga' },
  { title: 'Data e Hora', description: 'Quando deseja que a mensagem seja enviada?' },
  { title: 'Confirmação', description: 'Confirme aqui o envio' },
];

const pushSteps = [
  { title: 'Tipo de comunicação', description: 'Escolha o canal de comunicação' },
  { title: 'Mensagem', description: 'Edite a mensagem ou siga' },
  { title: 'Data e Hora - Promoção', description: 'Quando deseja que a mensagem seja enviada?' },
  { title: 'Confirmação', description: 'Confirme aqui o envio' },
];

function NotificationCentral() {
  const { colors } = useTheme();
  const navigate = useNavigate();

  const { formData } = useNotificationCentralContext();

  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  const previousStep = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
    else navigate('/central-de-notificacoes');
  };

  const nextStep = () => {
    if (activeStep < steps.length - 1) setActiveStep(activeStep + 1);
  };

  const Header = ({ title }: { title: string }) => (
    <Box display={'flex'} width={'100%'} alignItems={'center'}>
      <IconButton isRound={true} icon={<FaArrowLeft />} aria-label="Voltar" onClick={previousStep} />
      <Text
        fontSize={'xl'}
        marginRight={'auto'}
        marginLeft={3}
        textAlign={'left'}
        fontWeight={'bold'}
        textColor={colors.primaryColor}
      >
        {title}
      </Text>
    </Box>
  );

  const getMainContent = () => {
    switch (activeStep) {
      case 0:
        return <ChooseMethodStep onSubmit={nextStep} HeaderComponent={Header} />;
      case 1:
        return <ChooseReceiverStep onSubmit={nextStep} HeaderComponent={Header} />;
      case 2:
        return <ChooseTemplateStep onSubmit={nextStep} HeaderComponent={Header} />;
      case 3:
        return <EditMessageStep onSubmit={nextStep} HeaderComponent={Header} />;
      case 4:
        return <ChooseDateStep onSubmit={nextStep} HeaderComponent={Header} />;
      case 5:
        return <ConfirmEmailStep onSubmit={nextStep} HeaderComponent={Header} />;
      default:
        return <Text>Passo nao mapeado</Text>;
    }
  };

  const getPushContent = () => {
    switch (activeStep) {
      case 0:
        return <ChooseMethodStep onSubmit={nextStep} HeaderComponent={Header} />;
      case 1:
        return <EditPushStep onSubmit={nextStep} HeaderComponent={Header} />;
      case 2:
        return <ChooseDateStep onSubmit={nextStep} HeaderComponent={Header} promotion />;
      case 3:
        return <ConfirmPushStep onSubmit={nextStep} HeaderComponent={Header} />;
      default:
        return <Text>Passo nao mapeado</Text>;
    }
  };

  return (
    <DashboardTemplate>
      <AdaptiveStack spacing={10} padding={4} width={'100%'} justifyContent={'flex-start'} direction={'column'}>
        <Card overflowX="auto" padding={4}>
          <Stepper index={activeStep}>
            {formData.notificationType === 'push'
              ? pushSteps.map((step, index) => (
                  <Step minWidth={240} marginRight={'10px'} key={index} onClick={() => activeStep > index && setActiveStep(index)}>
                    <StepIndicator>
                      <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                    </StepIndicator>
                    <Box flexShrink="0" padding={2} width={'100%'}>
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>
                    <StepSeparator />
                  </Step>
                ))
              : steps.map((step, index) => (
                  <Step minWidth={240} key={index} onClick={() => activeStep > index && setActiveStep(index)}>
                    <StepIndicator>
                      <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                    </StepIndicator>
                    <Box flexShrink="0" padding={2} width={'100%'}>
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>
                    <StepSeparator />
                  </Step>
                ))}
          </Stepper>
        </Card>
        <Card padding={6} width={'100%'} transition="width 2s ease-in-out">
          <Flex justifyContent={'space-between'} alignItems={'flex-start'} flexDirection={'column'}>
            {formData.notificationType === 'push' ? getPushContent() : getMainContent()}
          </Flex>
        </Card>
      </AdaptiveStack>
    </DashboardTemplate>
  );
}

export function NotificationCentralWrapper() {
  return (
    <NotificationCentralProvider>
      <NotificationCentral />
    </NotificationCentralProvider>
  );
}
